import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, Validators } from '@angular/forms';
import { TranslationService } from '@sdbn/common';
import { BookingOffsetType, TransportOption, Weekday } from '../../transport-options.model';

@Component({
  selector: 'app-transport-option-editor-control',
  templateUrl: './transport-option-editor-control.component.html',
  styleUrls: ['./transport-option-editor-control.component.scss'],
  standalone: false
})
export class TransportOptionEditorControlComponent implements OnInit {

  formChanging = false;

  @Input() transportOption: TransportOption;

  @Output() changed = new EventEmitter();

  // hilfsvariable für color picker, direkter zugriff auf form.control.value geht bei nutzung von ahead of time compiler nicht
  colorPickerColor: string;

  keys = Object.keys;

  optionFormGroup = new FormGroup({});

  optionValid = false;

  bookingOffsetType = BookingOffsetType;
  bookingOffsetTypeOptions = new Array<BookingOffsetType>();
  weekdays = new Array<Weekday>();

  constructor(
    public translationService: TranslationService) {

    for (const key of Object.keys(BookingOffsetType)) {
      this.bookingOffsetTypeOptions.push(BookingOffsetType[key]);
    }

    for (const key of Object.keys(Weekday)) {
      this.weekdays.push(Weekday[key]);
    }

    this.optionFormGroup.addControl('permittedProcessingTime', new FormControl(null, []));
    this.optionFormGroup.addControl('onlyInternal', new FormControl(null, []));
    this.optionFormGroup.addControl('noProcessingTimeThreshold', new FormControl(null, []));
    this.optionFormGroup.addControl('globalConnectNotificationsEnabled', new FormControl(null, []));
    this.optionFormGroup.addControl('bookingOffsetType', new FormControl(BookingOffsetType.NONE, []));
    this.optionFormGroup.addControl('bookingOffsetHours', new FormControl(null, [Validators.required, Validators.min(0)]));
    this.optionFormGroup.addControl('bookingOffsetTime', new FormControl(null, [Validators.required]));
    this.optionFormGroup.addControl('bookingOffsetDays', new FormControl(null, [Validators.required, Validators.min(0)]));
    this.optionFormGroup.addControl('bookingOffsetWeeks', new FormControl(null, [Validators.required, Validators.min(0)]));
    this.optionFormGroup.addControl('bookingOffsetWeekday', new FormControl(null, [Validators.required]));
    this.optionFormGroup.addControl('colorPickerColor', new FormControl(null, []));
    this.optionFormGroup.addControl('preventGapsOnMultipleSlots', new FormControl(null, []));
    this.optionFormGroup.addControl('readOnlyAtTerminal', new FormControl(null, []))

    this.optionFormGroup.valueChanges.subscribe(
      value => {
        if (this.optionFormGroup.dirty && !this.formChanging) {
          this.transportOption.permittedProcessingTime = this.optionFormGroup.controls.permittedProcessingTime.value;
          this.transportOption.onlyInternal = this.optionFormGroup.controls.onlyInternal.value;
          this.transportOption.noProcessingTimeThreshold = this.optionFormGroup.controls.noProcessingTimeThreshold.value;
          this.transportOption.globalConnectNotificationsEnabled = this.optionFormGroup.controls.globalConnectNotificationsEnabled.value;
          this.transportOption.bookingOffsetType = this.optionFormGroup.controls.bookingOffsetType.value;
          this.transportOption.bookingOffsetHours = this.optionFormGroup.controls.bookingOffsetHours.value;
          this.transportOption.bookingOffsetTime = this.optionFormGroup.controls.bookingOffsetTime.value;
          this.transportOption.bookingOffsetDays = this.optionFormGroup.controls.bookingOffsetDays.value;
          this.transportOption.bookingOffsetWeeks = this.optionFormGroup.controls.bookingOffsetWeeks.value;
          this.transportOption.bookingOffsetWeekday = this.optionFormGroup.controls.bookingOffsetWeekday.value;
          this.transportOption.color = this.optionFormGroup.controls.colorPickerColor.value;
          this.transportOption.preventGapsOnMultipleSlots = this.optionFormGroup.controls.preventGapsOnMultipleSlots.value;
          this.transportOption.readOnlyAtTerminal = value.readOnlyAtTerminal;
          this.updateFieldEnablement();

          this.changed.emit();
        }
      }
    );
  }

  public ngOnInit() {

    const formdata: any = {};
    formdata.permittedProcessingTime = this.transportOption.permittedProcessingTime;
    formdata.onlyInternal = this.transportOption.onlyInternal;
    formdata.noProcessingTimeThreshold = this.transportOption.noProcessingTimeThreshold;
    formdata.globalConnectNotificationsEnabled = this.transportOption.globalConnectNotificationsEnabled;
    formdata.bookingOffsetType = this.transportOption.bookingOffsetType;
    formdata.bookingOffsetHours = this.transportOption.bookingOffsetHours;
    formdata.bookingOffsetTime = this.transportOption.bookingOffsetTime;
    formdata.bookingOffsetDays = this.transportOption.bookingOffsetDays;
    formdata.bookingOffsetWeeks = this.transportOption.bookingOffsetWeeks;
    formdata.bookingOffsetWeekday = this.transportOption.bookingOffsetWeekday;
    formdata.colorPickerColor = this.transportOption.color;
    formdata.preventGapsOnMultipleSlots = this.transportOption.preventGapsOnMultipleSlots;
    formdata.readOnlyAtTerminal = this.transportOption.readOnlyAtTerminal;

    this.optionFormGroup.reset(formdata)

    this.updateFieldEnablement();
  }

  public colorPickerChanged(color: string) {
    this.optionFormGroup.markAsDirty();
    this.transportOption.color = color;
    this.changed.emit();
  }

  private updateFieldEnablement() {
    this.formChanging = true;
    if (this.transportOption?.bookingOffsetType === BookingOffsetType.NONE) {
      this.optionFormGroup.controls.bookingOffsetHours.disable();
      this.optionFormGroup.controls.bookingOffsetTime.disable();
      this.optionFormGroup.controls.bookingOffsetDays.disable();
      this.optionFormGroup.controls.bookingOffsetWeeks.disable();
      this.optionFormGroup.controls.bookingOffsetWeekday.disable();
    } else if (this.transportOption?.bookingOffsetType === BookingOffsetType.HOURS) {
      this.optionFormGroup.controls.bookingOffsetHours.enable();
      this.optionFormGroup.controls.bookingOffsetTime.disable();
      this.optionFormGroup.controls.bookingOffsetDays.disable();
      this.optionFormGroup.controls.bookingOffsetWeeks.disable();
      this.optionFormGroup.controls.bookingOffsetWeekday.disable();
    } else if (this.transportOption?.bookingOffsetType === BookingOffsetType.TIME_AND_WORKDAYS) {
      this.optionFormGroup.controls.bookingOffsetHours.disable();
      this.optionFormGroup.controls.bookingOffsetTime.enable();
      this.optionFormGroup.controls.bookingOffsetDays.enable();
      this.optionFormGroup.controls.bookingOffsetWeeks.disable();
      this.optionFormGroup.controls.bookingOffsetWeekday.disable();
    } else if (this.transportOption?.bookingOffsetType === BookingOffsetType.WEEKDAY_AND_WEEK_OFFSET) {
      this.optionFormGroup.controls.bookingOffsetHours.disable();
      this.optionFormGroup.controls.bookingOffsetTime.enable();
      this.optionFormGroup.controls.bookingOffsetDays.disable();
      this.optionFormGroup.controls.bookingOffsetWeeks.enable();
      this.optionFormGroup.controls.bookingOffsetWeekday.enable();
    }
    this.formChanging = false;
  }

}
